import { cn } from "@babybjorn/ui";
import * as Sentry from "@sentry/gatsby";
import type Player from "@vimeo/player";
import { useCallback, useEffect, useRef, type FC } from "react";
import * as s from "./VimeoPlayer.module.scss";

type PlayerOptions = Player.Options & { vimeo_logo?: boolean };

type VimeoPlayerProps = {
	videoID: number;
	width?: number;
	height?: number;
	settings?: PlayerOptions;
	lazyLoad?: boolean;
};

// Global queue for player initialization to avoid overwhelming Vimeo API
const playerInitQueue: Array<() => Promise<void>> = [];
let isProcessingQueue = false;

const processQueue = async () => {
	if (isProcessingQueue || playerInitQueue.length === 0) return;

	isProcessingQueue = true;

	while (playerInitQueue.length > 0) {
		const initFn = playerInitQueue.shift();
		if (initFn) {
			try {
				await initFn();
				// Add delay between player initializations to avoid rate limiting
				await new Promise((resolve) => setTimeout(resolve, 100));
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						context:
							"Vimeo Player - Failed to initialize Vimeo player from queue",
					},
				});
			}
		}
	}

	isProcessingQueue = false;
};

const addToQueue = (initFn: () => Promise<void>) => {
	playerInitQueue.push(initFn);
	void processQueue();
};

const VimeoPlayer: FC<VimeoPlayerProps> = ({
	videoID,
	width,
	height,
	settings,
	lazyLoad = true,
}) => {
	const playerRef = useRef<Player | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const playerElementRef = useRef<HTMLDivElement>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const isDestroyedRef = useRef(false);
	const hasInitializedRef = useRef(false);
	const isEnqueuedRef = useRef(false);

	const initPlayer = useCallback(
		async (retryAttempt = 0): Promise<void> => {
			try {
				// Check if component was destroyed during async operation
				if (isDestroyedRef.current) return;

				// Ensure the player element exists
				if (!playerElementRef.current) {
					return;
				}

				const VimeoPlayer = await import("@vimeo/player");
				const PlayerCtor = VimeoPlayer.default;

				// Check again after import
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (isDestroyedRef.current) return;

				playerRef.current = new PlayerCtor(playerElementRef.current, {
					id: videoID,
					autoplay: true,
					loop: true,
					controls: false,
					playsinline: true,
					muted: true,
					title: false,
					byline: false,
					responsive: true,
					transparent: true,
					autopause: false,
					background: true,
					vimeo_logo: false,
					...settings,
				});

				// Check if component was destroyed before setting up event listeners
				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (isDestroyedRef.current) {
					await playerRef.current.destroy();
					return;
				}

				// Wait for player to be ready
				try {
					await playerRef.current.ready();
					// Mark as initialized
					hasInitializedRef.current = true;
				} catch (readyError) {
					// Player ready failed, but we still have a player instance
					Sentry.captureException(readyError, {
						extra: {
							context:
								"Vimeo Player - Player ready failed, but player instance exists",
						},
					});
					// Mark as initialized even if ready failed
					hasInitializedRef.current = true;
				}
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						context: "Vimeo Player - Failed to load Vimeo player",
					},
				});

				// Implement retry logic with exponential backoff (3 retries: 1s, 2s, 4s)
				const MAX_RETRIES = 3;
				if (retryAttempt < MAX_RETRIES) {
					const delay = Math.pow(2, retryAttempt) * 1000;

					setTimeout(() => {
						void initPlayer(retryAttempt + 1);
					}, delay);
				}
			} finally {
				// Clear the enqueued flag when initialization completes (success or failure)
				isEnqueuedRef.current = false;
			}
		},
		[videoID, settings],
	);

	useEffect(() => {
		// Return early on server
		if (typeof window === "undefined") {
			return;
		}

		let cancelled = false;

		const handleIntersection = (entries: IntersectionObserverEntry[]) => {
			const entry = entries[0];

			if (
				entry?.isIntersecting &&
				!hasInitializedRef.current &&
				!isEnqueuedRef.current &&
				!cancelled
			) {
				// Mark as enqueued to prevent duplicate jobs
				isEnqueuedRef.current = true;

				// Add to queue to avoid overwhelming Vimeo API
				addToQueue(async () => {
					if (!cancelled && !isDestroyedRef.current) {
						try {
							await initPlayer();
						} finally {
							// Even if init fails, initPlayer has its own retry;
							// clear enqueued flag to allow future attempts if needed.
							isEnqueuedRef.current = false;
						}
					} else {
						// Clear the enqueued flag if cancelled
						isEnqueuedRef.current = false;
					}
				});
			}
		};

		if (lazyLoad) {
			// Use Intersection Observer for lazy loading
			observerRef.current = new IntersectionObserver(handleIntersection, {
				rootMargin: "800px", // Start loading when 800px away from viewport
				threshold: 0.1,
			});

			if (containerRef.current) {
				observerRef.current.observe(containerRef.current);
			}
		} else {
			// Immediate initialization for non-lazy loading
			addToQueue(async () => {
				if (!cancelled && !isDestroyedRef.current) {
					await initPlayer();
				}
			});
		}

		return () => {
			// Set cancellation flags first to prevent new operations
			cancelled = true;
			isDestroyedRef.current = true;
			isEnqueuedRef.current = false;

			// Disconnect observer immediately
			if (observerRef.current) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}

			// Clear player refs immediately to prevent accidental reuse and help GC
			if (playerRef.current) {
				// Store reference for destruction, then clear immediately
				const playerToDestroy = playerRef.current;
				playerRef.current = null;

				// Destroy player asynchronously
				void playerToDestroy.destroy().catch(() => {
					// Ignore errors during destruction
				});
			}
		};
	}, [lazyLoad, initPlayer]);

	return (
		<div
			ref={containerRef}
			className={cn(s.wrapper)}
			style={{ aspectRatio: width && height ? `${width}/${height}` : "16/9" }}
		>
			<div className={s.videoPlayer} ref={playerElementRef}></div>
		</div>
	);
};

export default VimeoPlayer;
